/**
 * Assembly from a low-level {@link RawWorkbook}/{@link RawSheet} (named grids)
 * to a header-keyed {@link ParsedWorkbook} — the shape both the xlsx and CSV
 * adapters converge on (ADR-093 port/adapter). Product-agnostic: logical sheet
 * classification (which sheet is "Employees") is the consumer's concern and is
 * expressed through the optional `classify` hook, keeping this package free of
 * any domain vocabulary.
 */

import type {
  ParsedSheet,
  ParsedWorkbook,
  RawSheet,
  RawWorkbook,
} from './types.js';

/** Normalise a header for alias matching: trim, lowercase, collapse separators. */
export function normalizeHeaderKey(header: string): string {
  return header
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '_');
}

/**
 * Convert one {@link RawSheet} to a {@link ParsedSheet}. `grid[0]` is the header
 * row (headers trimmed); each subsequent row becomes an object keyed by header.
 * A later column with a duplicate header overwrites an earlier one. An empty
 * grid yields empty headers and no rows.
 *
 * `kind` defaults to the sheet's source name — the consumer can reclassify via
 * {@link toParsedWorkbook}'s `classify` hook.
 */
export function rawSheetToParsedSheet(
  sheet: RawSheet,
  kind?: string,
): ParsedSheet {
  const [, ...bodyRows] = sheet.grid;
  const headers = (sheet.grid.at(0) ?? []).map((h) => h.trim());
  const rows: Record<string, string>[] = bodyRows.map((row) => {
    const record: Record<string, string> = {};
    headers.forEach((header, col) => {
      record[header] = row.at(col) ?? '';
    });
    return record;
  });
  return {
    kind: kind ?? sheet.name,
    sourceName: sheet.name,
    headers,
    rows,
  };
}

/**
 * Assemble a {@link ParsedWorkbook} from a {@link RawWorkbook}. `classify`, if
 * given, maps a raw sheet to a logical `kind` (returning `null` drops the sheet
 * — e.g. a hidden `Lists` tab a consumer does not want in the result).
 */
export function toParsedWorkbook(
  raw: RawWorkbook,
  classify?: (sheet: RawSheet) => string | null,
): ParsedWorkbook {
  const sheets: ParsedSheet[] = [];
  for (const sheet of raw.sheets) {
    if (!classify) {
      sheets.push(rawSheetToParsedSheet(sheet));
      continue;
    }
    const kind = classify(sheet);
    if (kind === null) continue;
    sheets.push(rawSheetToParsedSheet(sheet, kind));
  }
  return { sheets };
}
